import { useState } from "react";
import { works } from "../../data/portfolioData";
import AnimatedSection from "../ui/AnimatedSection";
import { useInView } from "../../hooks/useInView";
import HoverVideoPreview from "../ui/HoverVideoPreview";

const CATEGORIES = ["all", "commercial", "brand", "social", "motion", "short form"];

function WorkCard({ work, index }) {
  const [ref, isVisible] = useInView();
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <div
      ref={ref}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "none" : "translateY(40px)",
        transition: `opacity 0.7s ease ${(index % 3) * 120}ms, transform 0.7s ease ${(index % 3) * 120}ms`,
      }}
    >
      <div className="group relative bg-carbon-900 border border-carbon-800 hover:border-ember-400/30 transition-all duration-500 overflow-hidden">

        {/* Video area — click opens modal */}
        <div
          className="relative overflow-hidden cursor-pointer"
          onClick={() => setModalOpen(true)}
        >
          {work.category.includes("short form") ? (
            <div className="relative overflow-hidden" style={{ paddingBottom: "0%" }}>
              <HoverVideoPreview videoId={work.youtubeId} title={work.title} aspectRatio="9/16" />
            </div>
          ) : (
            <HoverVideoPreview videoId={work.youtubeId} title={work.title} aspectRatio="16/9" />
          )}
          <div className="absolute top-3 left-3 z-30">
            <span className="bg-carbon-950/80 backdrop-blur-sm border border-carbon-700 font-mono text-xs text-ash-400 px-2 py-0.5 uppercase tracking-widest">
              {work.category[0]}
            </span>
          </div>
          {work.sharkTank && (
            <div className="absolute top-3 right-3 z-30">
              <span className="bg-ember-400/90 font-mono text-xs text-carbon-950 px-2 py-0.5 tracking-widest">
                🦈 Shark Tank India
              </span>
            </div>
          )}
          {work.featured && !work.sharkTank && (
            <div className="absolute top-3 right-3 z-30">
              <span className="bg-ember-400/90 font-mono text-xs text-carbon-950 px-2 py-0.5 uppercase tracking-widest">
                Featured
              </span>
            </div>
          )}
        </div>

        {/* Card content */}
        <div className="p-5">
          <div className="flex items-start justify-between gap-4 mb-3">
            <div>
              <p className="font-mono text-xs text-ash-600 tracking-widest uppercase mb-1">
                {work.client} · {work.year}
              </p>
              <h3 className="font-heading text-xl text-ash-100 tracking-wide leading-tight">
                {work.title}
              </h3>
            </div>
            <a
              href={`https://www.youtube.com/watch?v=${work.youtubeId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-shrink-0 w-8 h-8 border border-carbon-700 flex items-center justify-center hover:border-ember-400 hover:text-ember-400 text-ash-600 transition-all duration-300 mt-1"
              title="Watch on YouTube"
            >
              <svg
                width="10"
                height="10"
                viewBox="0 0 10 10"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              >
                <path d="M2 8L8 2M8 2H3M8 2v5" />
              </svg>
            </a>
          </div>
          <p className="font-body text-sm text-ash-600 leading-relaxed mb-4">
            {work.description}
          </p>
          <div className="flex flex-wrap gap-2">
            {work.tags.map((tag) => (
              <span
                key={tag}
                className="font-mono text-xs text-ash-600 border border-carbon-700 px-2 py-0.5 tracking-wide"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Modal */}
      {modalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-carbon-950/95 backdrop-blur-sm"
          onClick={() => setModalOpen(false)}
        >
          <div
            className="relative w-full mx-4"
            style={{ maxWidth: work.category.includes("short form") ? "400px" : "900px" }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setModalOpen(false)}
              className="absolute -top-10 right-0 font-mono text-xs text-ash-400 hover:text-ember-400 tracking-widest uppercase flex items-center gap-2"
            >
              Close ✕
            </button>
            <div
              className="relative border border-carbon-700"
              style={{ paddingBottom: work.category.includes("short form") ? "177.78%" : "56.25%" }}
            >
              <iframe
                key={modalOpen ? "open" : "closed"}
                src={`https://www.youtube.com/embed/${work.youtubeId}?autoplay=1&mute=0&rel=0&modestbranding=1&enablejsapi=1&playsinline=1`}
                title={work.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="absolute inset-0 w-full h-full border-0"
              />
            </div>
            <p className="mt-4 font-mono text-xs text-ash-600 tracking-widest uppercase text-center">
              {work.client} — {work.title}
            </p>
          </div>
        </div>
      )}

    </div>
  );
}

export default function WorkSection() {
  const [activeCategory, setActiveCategory] = useState("all");
  const [showAll, setShowAll] = useState(false);

  const filtered =
    activeCategory === "all"
      ? works
      : works.filter((w) => w.category.includes(activeCategory));

  const displayed =
    activeCategory === "all" && !showAll ? filtered.slice(0, 6) : filtered;

  const hasMore = activeCategory === "all" && !showAll && filtered.length > 6;

  return (
    <section id="work" className="py-24 lg:py-32 bg-carbon-950">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <AnimatedSection className="mb-16">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-8 h-px bg-ember-400" />
            <span className="font-mono text-xs text-ember-400 tracking-widest uppercase">
              Selected Work
            </span>
          </div>
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
            <h2 className="font-display text-[clamp(3rem,7vw,6rem)] leading-none tracking-wider text-ash-100">
              MY WORK
            </h2>
            <p className="font-body text-ash-400 max-w-md leading-relaxed">
              A curated selection of commercial, brand, and social media projects
              delivered for agencies and direct clients.
            </p>
          </div>
        </AnimatedSection>

        <AnimatedSection className="flex flex-wrap gap-3 mb-12" delay={100}>
          {CATEGORIES.map((cat) => (
            <button
              key={cat}
              onClick={() => {
                setActiveCategory(cat);
                setShowAll(false);
              }}
              className={`font-mono text-xs tracking-widest uppercase px-4 py-2 border transition-all duration-300 ${activeCategory === cat
                ? "border-ember-400 text-ember-400 bg-ember-400/10"
                : "border-carbon-700 text-ash-600 hover:border-ash-400 hover:text-ash-400"
                }`}
            >
              {cat}
            </button>
          ))}
        </AnimatedSection>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {displayed.map((work, i) => (
            <WorkCard key={work.id} work={work} index={i} />
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="py-24 text-center">
            <p className="font-body text-ash-600">No projects in this category yet.</p>
          </div>
        )}

        {hasMore && (
          <div className="mt-12 text-center">
            <button
              onClick={() => setShowAll(true)}
              className="font-mono text-xs tracking-widest uppercase px-8 py-3 border border-carbon-700 text-ash-400 hover:border-ember-400 hover:text-ember-400 transition-all duration-300"
            >
              Show All {filtered.length} Projects ↓
            </button>
          </div>
        )}

        {activeCategory === "all" && showAll && filtered.length > 6 && (
          <div className="mt-12 text-center">
            <button
              onClick={() => setShowAll(false)}
              className="font-mono text-xs tracking-widest uppercase px-8 py-3 border border-carbon-700 text-ash-400 hover:border-ember-400 hover:text-ember-400 transition-all duration-300"
            >
              Show Less ↑
            </button>
          </div>
        )}
      </div>
    </section>
  );
}